<!-- eslint-disable vue-a11y/form-has-label -->
<template>
  <div
    ref="textarea"
    class="sam-textarea"
    :class="textareaClassObj"
    :data-disabled="disabled && 'disabled'"
    :data-readonly="readonly && 'readonly'"
    :data-required="required && 'required'"
    data-samora
    :data-replicated-value="text || placeholder"
  >
    <textarea
      ref="realTextarea"
      v-model="text"
      class="sam-textarea__input"
      v-bind="$attrs"
      :disabled="disabled"
      :placeholder="placeholder"
      :readonly="readonly || asField"
      @input="interceptEvents"
      @change="interceptEvents"
      v-on="defaultListeners"
    />
  </div>
</template>
<script>
const CUSTOM_EVENTS = ['input', 'change'];

export default {
  name: 'SamTextarea',
  inheritAttrs: false,
  model: {
    prop: 'data',
  },
  props: {
    /**
     * The textarea's data variable
     */
    data: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * If the textarea is disabled (html attribute disabled)
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The textarea name (html attribute name)
     */
    name: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * The textarea's placeholder text
     */
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * textarea's resize direction
     * @values default, vertical, horizontal, none
     */
    resize: {
      type: String,
      required: false,
      default: 'default',
      validator(resize) {
        return ['default', 'vertical', 'horizontal', 'auto', 'none'].includes(resize);
      },
    },
    /**
     * If the textarea is on readonly state
     * @values true, false
     */
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the Textarea is required (html attribute required)
     * @values true, false
     */
    required: {
      type: Boolean,
      required: false,
      default: () => false,
    },
     /**
     * If the input should look as a field
     * @values true, false
     */
    asField: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      text: '',
    };
  },
  computed: {
    defaultListeners() {
      const listeners = { ...this.$listeners };

      CUSTOM_EVENTS.forEach((key) => delete listeners[key]);

      return listeners;
    },
    textareaClassObj() {
      return {
        'has-value': this.text !== '' || this.placeholder !== '',
        'has-vertical-resize': this.resize === 'vertical',
        'has-horizontal-resize': this.resize === 'horizontal',
        'has-no-resize': ['auto', 'none'].includes(this.resize),
        'is-auto-resize': this.resize === 'auto',
        'is-readonly': this.readonly,
        'as-field': this.asField
      };
    },
  },
  watch: {
    data: {
      handler(newVal) {
        this.text = newVal !== undefined && newVal !== null ? `${newVal}` : '';
      },
      immediate: true,
    },
  },
  methods: {
    interceptEvents(event) {
      if (CUSTOM_EVENTS.includes(event.type)) {
        event.stopPropagation();
        this.$emit(event.type, this.text);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.sam-textarea {
  @extend .px-4, .py-3, .d-inline-block;
  position: relative;
  height: fit-content;
  border: $size--0--fourth solid $neutral--60;
  border-radius: $size--1;
  background-color: $primary--white;
  $textarea: &;
  width: 100%;

  &:not([data-disabled]) {
    &:focus-within {
      border-color: $informative--border;
      outline: $informative--border 3px solid;
    }

    &:hover {
      border-color: $informative--hover;
    }
  }

  &.as-field {
    pointer-events: none;
    border:none;
    background-color: transparent;
    resize: none;
    padding-left: 0;
  }

  &[data-disabled='disabled'] {
    border-color: $neutral--40;
    background-color: $neutral--20;
    resize: none;

    #{textarea},
    #{textarea}__input {
      cursor: not-allowed;
    }
  }

  &[data-readonly='readonly'] {
    border-color: $neutral--40;
    outline: none;

    #{textarea},
    #{textarea}__input {
      cursor: unset;
    }
  }

  &__input {
    @extend .text--body-2, .py-0, .pl-0, .pr-1, .elevation--1;
    overflow: auto;
    min-height: calc(
      72px + 4px
    ); // The exact height of 3 lines of text (24px (line height) * 3 + 4px (padding))
    min-width: 240px;
    width: 100%;
    max-width: 100%;
    height: 100%;
    outline: none;
    border-width: 0;
    border-color: transparent;
    border-radius: $size--1;
    line-height: 24px;
    color: $pure--black;
    box-sizing: border-box;
    cursor: pointer;
    position: relative;
    transition: border $default-time $default-easing, outline $default-time $default-easing;
    background-color: inherit;

    &::-webkit-input-placeholder,
    &::-moz-placeholder,
    &:-ms-input-placeholder,
    &::-webkit-input-placeholder,
    &::placeholder {
      color: $neutral--60;
    }

    &:disabled {
      pointer-events: none;
      color: $neutral--60;
    }
  }

  &.has-vertical-resize {
    #{$textarea}__input {
      resize: vertical;
    }
  }

  &.has-horizontal-resize {
    #{$textarea}__input {
      resize: horizontal;
    }
  }

  &.has-no-resize {
    #{$textarea}__input {
      resize: none;
    }
  }

  // from https://css-tricks.com/the-cleanest-trick-for-autogrowing-textareas/
  &.is-auto-resize {
    display: grid;

    &::after {
      content: attr(data-replicated-value) ' ';
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow-wrap: break-word;
      visibility: hidden;
    }

    > textarea {
      resize: none;
      overflow: hidden;
    }

    > textarea,
    &::after {
      @extend .text--body-2, .py-0, .pl-0, .pr-1;
      grid-area: 1 / 1 / 2 / 2;
    }
  }

  &.is-readonly,
  textarea:read-only {
    border-color: $neutral--60 !important;
    outline: none !important;
  }
}
</style>
